import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import {
  CreateLocalCredentialDialogComponent
} from "./dialogs/create-local-credential-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  CreateProxyCredentialDialogComponent
} from "./dialogs/create-proxy-credential-dialog.component";
import { OrganisationService } from "@fry/lib/organisations";
import { SecurityService } from "@fry/lib/security";
import { SYSTEM_ROLE_ID } from "@fry/lib/roles/roles.system";
import {
  UpdateCredentialUsernameDialogComponent
} from "@fry/components/accounts/credential/dialogs/update-credential-username-dialog.component";
import {
  UpdateCredentialPasswordDialogComponent
} from "@fry/components/accounts/credential/dialogs/update-credential-password-dialog.component";
import { MatTab, MatTabGroup } from "@angular/material/tabs";
import {
  CredentialTypes,
  ApiCredential,
  LocalCredential,
  ProxyCredential,
  READABLE_CREDENTIAL_TYPES
} from "@fry/lib/auth/account/credential/credential";
import { take } from "rxjs/operators";
import { Observable, of, switchMap } from "rxjs";

import { CredentialService } from "@fry/lib/auth/account/credential/credential.service";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { DialogComponent } from "@fry/components/common/dialog";
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { isDialogError } from "@fry/components/common/dialog/dialog.error";
import { MFAMethod } from "@fry/lib/auth/account/mfa/mfa";
import { OidcService } from '@fry-it/risr-core';


@Component({
    selector: 'eas-credentials',
    templateUrl: './list-credentials.component.html',
    styleUrls: ['./list-credentials.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    MatTabGroup,
    MatTab,
    MatCardModule,
    MatButtonModule,
    RouterLink,
    MatTab
]
})
export class ListCredentialsComponent {
  public remoteUser: string | null = null;
  public isForRemoteUser: boolean = false;
  private organisationId: string = '';
  private isAdmin: boolean = false;
  private isSuperAdmin: boolean = false;
  private hasManagePermission: boolean = false;

  allowedAccountTypes: (CredentialTypes | '__all__')[] = [];
  private enabledMFAs: MFAMethod[] = [];
  loginCredentials: (LocalCredential | ProxyCredential)[] = [];
  apiCredentials: ApiCredential[] = [];
  readableName = READABLE_CREDENTIAL_TYPES;
  newlyCreatedAPICredential: ApiCredential | null = null;

  protected readonly CredentialTypes = CredentialTypes;

  currentHref: string = '';

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private organisationService: OrganisationService,
    private securityService: SecurityService,
    private credentialService: CredentialService,
    private cdr: ChangeDetectorRef,
    private oidc: OidcService,
  ) {
  }

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const userParam = params.get('user') || '';
      if (userParam !== 'profile') {
        this.remoteUser = userParam;
      }
      this.currentHref = `/users/${userParam}/credentials`;

      this.initializeAfterAccountId();
    });
  }

  private initializeAfterAccountId() {
    this.loadCredentials();
    this.setOrganisationAllowedAccountTypes();
    this.setPermissions()
  }

  // Content loading
  private loadCredentials() {
    this.credentialService.getCredentials(this.remoteUser).subscribe({
      next: (credentials) => {
        this.loginCredentials = credentials
          .filter(cred => cred.type !== CredentialTypes.API) as (LocalCredential | ProxyCredential)[];

        this.apiCredentials = credentials
          .filter(cred => cred.type === CredentialTypes.API) as ApiCredential[];

        this.isForRemoteUser = !!this.remoteUser;

        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load credentials:', error);
      }
    });
  }

  public getMfaHref(username: string){
    return `${this.currentHref}/${username}/mfas`;
  }

  private setOrganisationAllowedAccountTypes() {
    this.organisationService.getCurrent().subscribe(org => {
      this.organisationId = org.doc._id;
      this.allowedAccountTypes = org.allowedAccountTypes;
      this.enabledMFAs = org.enabledMFAs;
    });
  }

  // Permissions
  private async setPermissions() {
    this.securityService.hasRole(SYSTEM_ROLE_ID.SUPER_ADMIN).subscribe(hasRole => {
      this.isSuperAdmin = hasRole;
    });

    this.securityService.hasRole(SYSTEM_ROLE_ID.ADMIN).subscribe(hasRole => {
      this.isAdmin = hasRole;
    });

    const managePermission = this.isForRemoteUser ? 'credential.manage' : 'credential.manage.own';
    this.securityService.hasPermission(managePermission).subscribe(hasRole => {
      this.hasManagePermission = hasRole;
    });
  }

  canAccessTwoFactorAuth(type: CredentialTypes.LOCAL | CredentialTypes.PROXY) {
    return type === CredentialTypes.LOCAL && this.enabledMFAs.length > 1;
  }

  canCreateCredential(type: CredentialTypes): boolean {
    if (type == CredentialTypes.PROXY) {
      return this.isAdmin;
    }

    if (type === CredentialTypes.API) {
      return this.isSuperAdmin;
    }

    return this.allowedAccountTypes.includes(type) && this.hasManagePermission;
  }

  canUpdateUsername(type: CredentialTypes): boolean {
    return type === CredentialTypes.LOCAL && this.hasManagePermission;
  }

  canUpdateSSOID(type: CredentialTypes): boolean {
    return type === CredentialTypes.PROXY && this.isAdmin;
  }

  canSendPasswordEmail(type: CredentialTypes): boolean {
    return type === CredentialTypes.LOCAL && this.hasManagePermission && this.isForRemoteUser;
  }

  canUpdatePassword(type: CredentialTypes): boolean {
    return type === CredentialTypes.LOCAL && (!this.isForRemoteUser || this.hasManagePermission);
  }

  canAccessAPIKeys(): boolean {
    return this.isAdmin;
  }

  canDelete(): boolean {
    if (this.isForRemoteUser) {
      return this.hasManagePermission;
    } else {
      return this.hasManagePermission && this.loginCredentials.length > 1
    }
  }

  // Actions
  async addLoginCredential(type: CredentialTypes) {
    await this.oidc.ensureStepup();

    if (type === CredentialTypes.LOCAL) {
      this.createLocalCredential();
    } else if (type === CredentialTypes.PROXY) {
      this.createProxyCredential();
    }
  }

  private createLocalCredential() {
    const dialogRef = this.dialog.open(CreateLocalCredentialDialogComponent, {
      width: '600px',
      disableClose: true,
      data: { isForRemoteUser: this.isForRemoteUser }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        const localCredential: LocalCredential = {
          type: CredentialTypes.LOCAL,
          organisationId: this.organisationId,
          username: result.username,
          password: result.password
        };
        const observable = this.credentialService.createCredential(localCredential, this.remoteUser);
        this.handleResponse(observable, 'Local credential added successfully!', 'Failed to add Local credential');
      }
    });
  }

  private createProxyCredential() {
    const dialogRef = this.dialog.open(CreateProxyCredentialDialogComponent, {
      width: '600px',
      disableClose: true,
      data: { isForRemoteUser: this.isForRemoteUser }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (!result || isDialogError(result)) {
        return of(null);
      }

      const proxyCredential: ProxyCredential = {
        type: CredentialTypes.PROXY,
        organisationId: this.organisationId,
        username: result.username
      };

      const observable = this.credentialService.createCredential(proxyCredential, this.remoteUser);
      return this.handleResponse(observable, 'SSO credential created successfully!', 'Failed to add SSO credential');
    });
  }

  async createApiCredential() {
    await this.oidc.ensureStepup();
    const apiCredential: ApiCredential = {
      type: CredentialTypes.API
    };

    const observable = this.credentialService.createCredential(apiCredential, this.remoteUser);
    this.handleResponse(observable, 'API credential created successfully!', 'Failed to add API credential')
      .then(response => {
        this.newlyCreatedAPICredential = response as ApiCredential;
      });
  }

  copyToClipboard() {
    if (this.newlyCreatedAPICredential) {
      const text = `public_key: ${this.newlyCreatedAPICredential.public_key}\nsecret_key: ${this.newlyCreatedAPICredential.secret_key}`;
      navigator.clipboard.writeText(text).then(() => {
        this.snackBar.open('API credentials copied to clipboard!', 'Close', { duration: 3000 });
      });
    }
  }

  async updateUsername(credential: LocalCredential | ProxyCredential) {
    await this.oidc.ensureStepup();
    const dialogRef = this.dialog.open(UpdateCredentialUsernameDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        organisationId: this.organisationId,
        currentCredentialUsername: credential.username,
        credentialType: credential.type
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (!result) {
        return of(null);
      }

      const currentCredentialUsername = credential.username || '';
      const observable = this.credentialService.updateCredentialUsername(
        credential.type,
        currentCredentialUsername,
        result.newUsername,
        this.remoteUser
      )
      return this.handleResponse(observable, 'Username updated successfully!', 'Failed to update username');
    });
  }

  async updatePassword(localCredential: LocalCredential) {
    await this.oidc.ensureStepup();
    const dialogRef = this.dialog.open(UpdateCredentialPasswordDialogComponent, {
      width: '400px',
      disableClose: true,
      data: { username: localCredential.username }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (!result) {
        return of(null);
      }

      const { newPassword, passwordConfirmation } = result;
      const observable = this.credentialService.updatePassword(
        localCredential.username || '',
        newPassword,
        passwordConfirmation,
        this.remoteUser,
      )
      return this.handleResponse(observable, 'Password updated successfully!', 'Failed to update password');
    });
  }

  async deleteCredential(credential: LocalCredential | ProxyCredential | ApiCredential) {
    await this.oidc.ensureStepup();
    const dialog = this.dialog.open(DialogComponent, {
      data: {
        title: $localize`Are you sure?`,
        content: $localize`Are you sure you want to delete this credential? This action cannot be
         undone.`,
        okButton: $localize`Yes, delete it!`,
        cancelButton: $localize`No, keep it`
      }
    });
    return dialog.afterClosed().pipe(
      switchMap(result => {
        if (!result || isDialogError(result)) {
          return of(null);
        }

        const observable = credential.type === CredentialTypes.API
          ? this.credentialService.deleteApiCredential(credential.public_key || '', this.remoteUser)
          : this.credentialService.deleteLoginCredential(credential, this.remoteUser);

        return this.handleResponse(observable, 'Credential deleted successfully!', 'Failed to delete credential')
          .then(() => {
            if (credential.type === CredentialTypes.API) {
              this.newlyCreatedAPICredential = null;
            }
          });
      })
    ).subscribe();
  }

  async sendPasswordEmail(localCredential: LocalCredential) {
    await this.oidc.ensureStepup();
    const dialog = this.dialog.open(DialogComponent, {
      data: {
        title: $localize`Are you sure?`,
        content: $localize`This will send an email to this user containing a link for them to
        follow to reset their password. Would you like to continue?`,
        okButton: $localize`Yes, send it!`,
        cancelButton: $localize`No, cancel`
      }
    });
    return dialog.afterClosed().pipe(
      switchMap(result => {
        if (!result || isDialogError(result)) {
          return of(null);
        }

        const observable = this.credentialService.sendPasswordResetEmail(
          this.remoteUser as string,
          localCredential.username || ''
        );
        return this.handleResponse(observable, 'An email would be sent shortly!', 'Failed');
      })
    ).subscribe();
  }

  async handleResponse(observable: Observable<any>, successMessage: string, errorMessage: string): Promise<any> {
    return new Promise((resolve, reject) => {
      observable.pipe(take(1)).subscribe({
        next: (response) => {
          this.snackBar.open(successMessage, 'Close', { duration: 3000 });
          this.loadCredentials();
          resolve(response);
        },
        error: (err) => {
          this.snackBar.open(`${errorMessage}: ${err}. Please try again later.`, 'Close', { duration: 3000 });
          reject(err);
        }
      });
    });
  }
}
